import 'polyfills';
import App, { AppProps } from 'next/app';
import Head from 'next/head';
import React, { ErrorInfo } from 'react';
import { Tooltip as TooltipPrimitive } from 'radix-ui';
import { Provider } from 'react-redux';
import { Store } from 'redux';
import { withReduxStore } from 'store';
import { sendErrorInfoToApi } from 'lib/api';
import { isRouteDisabled } from 'lib/routing';
import { PendingJobsPoller } from 'components/app';
import { ThemeEditorProvider } from 'components/app/ThemeEditorContext';
import { AppSignalErrorBoundary } from 'components/appsignal';
import { ErrorPage } from 'components/error';

interface Props extends AppProps {
  isAppError?: boolean;
  reduxStore: Store;
}

// styles
import 'public/static/fonts/source-sans-pro/source-sans-pro.css';
import 'croppr/dist/croppr.css';
import 'emoji-mart/css/emoji-mart.css';
import 'katex/dist/katex.css';
import 'plyr/dist/plyr.css';
import 'react-datepicker/dist/react-datepicker.css';
import '@fortawesome/fontawesome-svg-core/styles.css';
import 'highlight.js/styles/a11y-light.css';

class MyApp extends App<Props> {
  async componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { user } = this.props.reduxStore.getState();

    await sendErrorInfoToApi({
      error,
      errorInfo,
      user: { id: user?.id, roles: user?.roles }
    });
  }

  render() {
    const { Component, isAppError, pageProps, reduxStore, router } = this.props;
    const { language, serverConfig } = reduxStore.getState();

    return (
      <>
        <Head>
          {serverConfig?.serverDisableIndexing ? (
            <meta name="robots" content="noindex" />
          ) : null}
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>
            {serverConfig?.siteTitleTranslations?.[language] ||
              serverConfig?.siteTitle ||
              'Vuolearning'}
          </title>
        </Head>

        <Provider store={reduxStore}>
          <PendingJobsPoller />
          <AppSignalErrorBoundary name="AppErrorBoundary">
            {isAppError ? (
              <ErrorPage appError={true} />
            ) : isRouteDisabled(router.pathname, serverConfig) ? (
              <ErrorPage statusCode={404} />
            ) : (
              <ThemeEditorProvider>
                <TooltipPrimitive.Provider>
                  <Component {...pageProps} />
                </TooltipPrimitive.Provider>
              </ThemeEditorProvider>
            )}
          </AppSignalErrorBoundary>
        </Provider>
      </>
    );
  }
}

export type MyAppType = typeof MyApp;
export default withReduxStore(MyApp);
